import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  doc,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  setDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPaperPlane, FaRobot, FaUser, FaVolumeUp, FaVolumeMute, FaMicrophone, FaStop, FaPlay, FaPause, FaCheck, FaHeart, FaReply, FaTimes, FaImage } from "react-icons/fa";
import * as tf from '@tensorflow/tfjs';
import { SYMPTOM_ANALYSIS_PROMPT, FISH_DISEASE_CLASSES } from "../constants/fish_disease_classes";

const getOrCreateCustomerId = () => {
  let customerId = localStorage.getItem("customerId");
  if (!customerId) {
    customerId = "guest_" + Math.random().toString(36).substr(2, 9);
    localStorage.setItem("customerId", customerId);
  }
  return customerId;
};

// ─── Gemini Vision Quota Helpers ─────────────────────────────────────────────
const VISION_DAILY_LIMIT = 5;
// ─────────────────────────────────────────────────────────────────────────────

// Sound notification functions
const playMessageSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);

    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.log("Sound not supported");
  }
};

const playSendSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.1);

    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.2);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch (error) {
    console.log("Sound not supported");
  }
};

// Fetch all products from Firestore
const fetchAllProducts = async () => {
  try {
    const [fish, aquariums, plants, foods] = await Promise.all([
      getDocs(collection(db, 'guppies')).then(snap => snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))),
      getDocs(collection(db, 'aquariums')).then(snap => snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))),
      getDocs(collection(db, 'plants')).then(snap => snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))),
      getDocs(collection(db, 'food')).then(snap => snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })))
    ]);

    return { fish, aquariums, plants, foods };
  } catch (error) {
    console.error("Error fetching products:", error);
    return { fish: [], aquariums: [], plants: [], foods: [] };
  }
};

// Enhanced Product List Generator for Guppies with proper price and stock handling
// Enhanced Product List Generator with Arabic fish name translation
const generateProductList = (products, type) => {
  if (products.length === 0) return `لا توجد منتجات متوفرة حالياً في فئة ${type}`;

  // Fish name translation dictionary
  const fishNameTranslations = {
    'guppy': 'جوبي',
    'betta': 'بيتا',
    'angel': 'أنجل',
    'neon': 'نيون',
    'molly': 'مولي',
    'platy': 'بلاتي',
    'swordtail': 'سيف الذيل',
    'tetra': 'تترا',
    'goldfish': 'سمك ذهبي',
    'corydoras': 'كوريدوراس',
    'danio': 'دانيو',
    'barb': 'بارب',
    'oscar': 'أوسكار',
    'discus': 'ديسكس',
    'cichlid': 'سيكليد'
  };

  // Function to translate fish names
  const translateFishName = (englishName) => {
    if (!englishName) return 'غير مسمى';

    const lowercaseName = englishName.toLowerCase();
    for (const [english, arabic] of Object.entries(fishNameTranslations)) {
      if (lowercaseName.includes(english)) {
        return arabic + (lowercaseName !== english ? ` (${englishName})` : '');
      }
    }
    return englishName; // Return original if no translation found
  };

  return products.map(product => {
    let productName = product.name || 'غير مسمى';

    // Translate fish names to Arabic if it's a fish product
    if (type === "أسماك زينة") {
      productName = translateFishName(productName);
    }

    let productInfo = `- ${productName} (${type})`;

    // Handle guppies with specific price and stock logic
    if (type === "أسماك زينة") {
      const availableOptions = [];

      // Check if it's a singular fish or couple-based
      if (product.singular) {
        // Singular fish - check female availability and price
        if (product.femalecount > 0 && product.femaleprice > 0) {
          availableOptions.push(`أنثى: ${product.femaleprice} دج (متوفر: ${product.femalecount})`);
        }

        // Check male availability and price
        if (product.malecount > 0 && product.maleprice > 0) {
          availableOptions.push(`ذكر: ${product.maleprice} دج (متوفر: ${product.malecount})`);
        }
      } else {
        // Couple-based fish
        if (product.couplecount > 0 && product.coupleprice > 0) {
          availableOptions.push(`زوج: ${product.coupleprice} دج (متوفر: ${product.couplecount} زوج)`);
        }
      }

      // Add availability info
      if (availableOptions.length > 0) {
        productInfo += `\n  الأسعار والمتوفر:\n    ${availableOptions.join('\n    ')}`;
      } else {
        productInfo += `\n  الحالة: غير متوفر حالياً`;
      }

      // Check if sold out
      if (product.sold_status) {
        productInfo += `\n  ⚠️ تم البيع - اتصل للتأكد من التوفر`;
      }

      // Add fish number if available
      if (product.guppynumber) {
        productInfo += `\n  رقم السمكة: ${product.guppynumber}`;
      }

    } else {
      // Handle other product types (aquariums, plants) with original logic
      if (product.price) {
        productInfo += `\n  السعر: ${product.price} دج`;
      } else {
        productInfo += `\n  السعر: يرجى الاستفسار`;
      }

      // Add additional details if available
      if (product.size) productInfo += `\n  الحجم: ${product.size}`;
      if (product.type) productInfo += `\n  النوع: ${product.type}`;
      if (product.color) productInfo += `\n  اللون: ${product.color}`;
      if (product.description) productInfo += `\n  الوصف: ${product.description}`;
    }

    return productInfo;
  }).join('\n\n');
};

// Generate fish food info for chatbot
const generateFishFoodInfo = (foods) => {
  const fishFood = foods.find(f => (f.name && f.name.toLowerCase().includes('سمك')) || (f.name && f.name.toLowerCase().includes('fish food')));
  if (!fishFood) {
    return `
**طعام الأسماك:**
🍽️ طعام أسماك مميز متوفر:
- المكونات: مسحوق سمك، سبيرولينا، بيتاكاروتان، سبانخ، بيض مجفف، أوميغا 3، فيتامينات C E A B، سيلينيوم، ثوم، كالسيوم، خميرة البيرة
- الفوائد: نمو و تلوين + رفع المناعة
- الأشكال المتوفرة: 
  * حبات صغار (1 ملم) - للأسماك التي تفضل الحبات
  * مسحوق (غبرة) - للأسماك الصغيرة والفراخ
- السعر: 500 دج لكل 50غ
- الكميات المتاحة: 50غ، 100غ، 150غ، 200غ... (مضاعفات 50غ)
- حساب السعر: كل 50غ = 500 دج (مثلاً: 100غ = 1000 دج، 150غ = 1500 دج)
`;
  }

  const basePrice = fishFood.basePricePer50g || 500;
  const powderAvailable = fishFood.powderAvailable !== false;
  const grainsAvailable = fishFood.grainsAvailable !== false;

  return `
**طعام الأسماك:**
🍽️ ${fishFood.name || 'طعام أسماك مميز'}:
- المكونات: ${fishFood.description || 'مسحوق سمك، سبيرولينا، بيتاكاروتان، سبانخ، بيض مجفف، أوميغا 3، فيتامينات C E A B، سيلينيوم، ثوم، كالسيوم، خميرة البيرة'}
- الفوائد: ${fishFood.benefits || 'نمو و تلوين + رفع المناعة'}
- الأشكال المتوفرة: 
  ${grainsAvailable ? '* حبات صغار (1 ملم) - للأسماك التي تفضل الحبات' : ''}
  ${powderAvailable ? '* مسحوق (غبرة) - للأسماك الصغيرة والفراخ' : ''}
- السعر: ${basePrice} دج لكل 50غ
- الكميات المتاحة: 50غ، 100غ، 150غ، 200غ... (مضاعفات 50غ)
- حساب السعر: كل 50غ = ${basePrice} دج (مثلاً: 100غ = ${basePrice * 2} دج، 150غ = ${basePrice * 3} دج)
`;
};

// Optimized system prompt for faster, more concise responses
const createSystemPrompt = (fish, aquariums, plants, foods, conversationContext, greetingInstruction) => {
  const fishFoodInfo = generateFishFoodInfo(foods || []);

  // Filter out sold out and 0-quantity products
  const availableFish = fish.filter(f => !f.sold_status && (f.malecount > 0 || f.femalecount > 0 || f.couplecount > 0));
  const availableAquariums = aquariums.filter(a => !a.sold_status && (a.quantity === undefined || a.quantity > 0));
  const availablePlants = plants.filter(p => !p.sold_status && (p.quantity === undefined || p.quantity > 0));

  const fishListText = generateProductList(availableFish, "أسماك زينة");
  const aquariumsListText = generateProductList(availableAquariums, "أحواض");
  const plantsListText = generateProductList(availablePlants, "نباتات");

  return `
أنت مساعد ذكي لمتجر كليكفيش دي زاد المتخصص في أسماك الزينة والنباتات المائية.

${greetingInstruction}

**معلومات أساسية:**
- المالك: واضح أمين (21 سنة، متخصص في علوم الكمبيوتر وهاوي أسماك محترف)
- الموقع: شلغوم العيد (نخدم قسنطينة والولايات المجاورة)
- الهاتف: +213781219669
- الموقع: https://clickfishdz.shop/
- ساعات العمل: 9 صباحاً - 8 مساءً

**المنتجات المتوفرة حالياً فقط (لا تقترح أي منتج غير موجود في هذه القائمة):**
${fishListText}

${aquariumsListText}

${plantsListText}

${fishFoodInfo}

**قواعد العمل بنظام الطلبات (هام جداً):**
- إذا أعجب العميل بمنتج وأراد الشراء، اطلب منه معلومات التوصيل: (الاسم، الهاتف، الولاية والبلدية).
- بمجرد حصولك على المعلومات الكاملة والمنتجات المحددة، أضف هذا المربع المخفي (JSON) في نهاية رسالتك لكي يقوم النظام بتسجيل الطلب:
\`\`\`json
{
  "ORDER_READY": true,
  "customerName": "...",
  "customerPhone": "...",
  "customerAddress": "...",
  "items": [{ "name": "...", "type": "guppy", "quantity": 1, "price": 0 }],
  "totalPrice": 0
}
\`\`\`
- اخبر العميل أيضاً في نفس رسالتك بأن الطلب تم تحضيره وأنه سيتم التواصل معه قريباً.
- لا تنسى الرد بلباقة.

**قواعد مهمة:**
1. أجب باختصار ووضوح (3-5 جمل كحد أقصى إلا إذا طلب التفاصيل)
2. اذكر الأسعار والكميات المتوفرة مباشرة
3. اسم المتجر هو ClickFishDZ أو "كليك فيش"، إياك أن تستخدم اسم DZ Guppy أو GuppyDZ.
4. الأسماك: بعضها منفرد (ذكر/أنثى) وبعضها أزواج فقط
5. خصم 10% عند شراء 5+ أزواج
5. طعام الأسماك: متوفر بحبات (1ملم) ومسحوق، السعر حسب الكمية (500 دج لكل 50غ)
6. نصائح العناية: درجة الحرارة 24-26°C، تغيير 25% ماء أسبوعياً
7. طرق الدفع: CCP، Baridimob، الدفع عند الاستلام
8. التوصيل: متوفر لجميع الولايات

**أولوية الرد:**
- الإجابة المباشرة أولاً
- ثم التفاصيل الإضافية عند الحاجة
- تجنب الإطالة غير الضرورية

${conversationContext ? `**سياق المحادثة الأخيرة:**\n${conversationContext}\n` : ''}
`;
};

// Helper to get active Gemini API key, filtering out any suspended keys
const getActiveGeminiKey = (index = 0) => {
  const keys = [
    process.env.REACT_APP_GEMINI_API_KEY,
    process.env.REACT_APP_GEMINI_FALLBACK_KEY,
    process.env.REACT_APP_GEMINI_FALLBACK_KEY_3,
    "AQ.Ab8RN6LUZsnWcTOwcPAwVedoPF1hd5Sfdv053jJXerME-i5wVQ",
    "AQ.Ab8RN6JZfL6kG_dWAT_Yl45EOXFeV_dUpCNE71unV-vNjjhLYQ",
    "AQ.Ab8RN6KxGK8D-Kohpg1mh1zeio3sAVtB7pNXJh1FzIWNk8V0NA"
  ].filter(k => k && !k.startsWith('AIzaSyBVxQ') && !k.startsWith('AIzaSyBK5ca') && !k.includes('YOUR_'));
  
  const uniqueKeys = [...new Set(keys)];
  return uniqueKeys[index] || uniqueKeys[0];
};

// Fallback: Second Gemini key (same model, different quota pool)
const callGeminiFallback = async (systemPrompt, userText) => {
  try {
    const fallbackKey = getActiveGeminiKey(1);
    if (!fallbackKey) {
      console.log("No Gemini fallback key configured, skipping.");
      return null;
    }

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${fallbackKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system_instruction: {
            parts: [{ text: systemPrompt }]
          },
          contents: [
            { role: "user", parts: [{ text: userText }] },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
            candidateCount: 1,
          },
        }),
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(`Gemini Fallback Error: ${data.error?.message || 'Unknown error'}`);
    }
    return data?.candidates?.[0]?.content?.parts?.[0]?.text || null;
  } catch (error) {
    console.error("Error calling Gemini fallback:", error);
    return null;
  }
};

// Last-resort Gemini fallback (3rd key) with heavily Darja-tuned prompt
const callGeminiFallback3 = async (systemPrompt, userText) => {
  try {
    const apiKey = getActiveGeminiKey(2);
    if (!apiKey) {
      return null;
    }

    // Inject strict Darja instructions on top of the system prompt
    const darjaPrefix = `IMPORTANT: You MUST respond ONLY in Algerian Darja dialect written with Arabic letters. Examples:
- Say "واش راك" not "كيف حالك"
- Say "شحال" not "كم"
- Say "قداش" not "كم السعر"
- Say "راني نعاونك" not "أنا هنا للمساعدة"
- Say "بصح" not "لكن"
- Say "مزيان" not "جيد"
NEVER use Modern Standard Arabic. NEVER use French. ALWAYS use Algerian street dialect. STRICT RULE: Your store name is "ClickFishDZ" (or "كليك فيش دي زاد"). NEVER introduce yourself as DZ Guppy. You MUST state that the website is https://clickfishdz.shop/ if they ask.

`;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${apiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system_instruction: {
            parts: [{ text: darjaPrefix + systemPrompt }]
          },
          contents: [
            { role: "user", parts: [{ text: userText }] },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
            candidateCount: 1,
          },
        }),
      }
    );

    const data = await response.json();
    if (!response.ok) throw new Error(`Gemini Fallback 3 Error: ${data.error?.message || 'Unknown'}`);
    return data?.candidates?.[0]?.content?.parts?.[0]?.text || null;
  } catch (error) {
    console.error("Error calling Gemini fallback 3 (last resort):", error);
    return null;
  }
};

// High-speed reliable fallback using Groq
const callGroqFallback = async (systemPrompt, userText) => {
  try {
    const groqKey = process.env.REACT_APP_GROQ_API_KEY;
    if (!groqKey) {
      console.log("No Groq fallback key configured, skipping.");
      return null;
    }

    const response = await fetch("https://api.groq.com/openai/v1/chat/completions", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${groqKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        model: "openai/gpt-oss-120b",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userText }
        ],
        temperature: 0.7,
        max_tokens: 2048
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(`Groq Fallback Error: ${data.error?.message || 'Unknown error'}`);
    }
    return data?.choices?.[0]?.message?.content || null;
  } catch (error) {
    console.error("Error calling Groq fallback:", error);
    return null;
  }
};

// AI Assistant Webhook Call with conversation context and product handling
const callChatAssistant = async (userText, conversationHistory = [], hasGreeted = false, imageData = null) => {
  try {
    // Fetch all products first
    const { fish, aquariums, plants, foods } = await fetchAllProducts();

    // Build optimized conversation context (last 3 messages only for speed)
    const conversationContext = conversationHistory.length > 0
      ? conversationHistory.slice(-3).map(msg =>
        `${msg.sender === 'customer' ? 'عميل' : 'أنت'}: ${(msg.text || (msg.messageType === 'image' ? '🖼️ صورة' : '🎤 صوت')).substring(0, 100)}`
      ).join('\n')
      : '';

    const greetingInstruction = hasGreeted
      ? "تجنب تكرار الترحيب في كل رد. ركز على الإجابة المباشرة على السؤال."
      : "يمكنك الترحيب بالعميل مرة واحدة فقط في بداية المحادثة.";

    let systemPrompt = createSystemPrompt(fish, aquariums, plants, foods, conversationContext, greetingInstruction);

    const isDiagnostic = imageData || userText.includes('نموذج الذكاء الاصطناعي') || userText.includes('Local AI Model');
    if (isDiagnostic) {
      systemPrompt += `\n\n${SYMPTOM_ANALYSIS_PROMPT}`;
    }

    let finalUserText = userText;
    if (imageData) {
      finalUserText = `[IMAGE ATTACHED] ${userText || "Please analyze this fish for any diseases or symptoms."}`;
    }

    const messageParts = [
      { text: finalUserText }
    ];

    // Add image data for Gemini Vision if provided
    if (imageData) {
      // Extract base64 without prefix if present
      const base64Data = imageData.includes(',') ? imageData.split(',')[1] : imageData;
      messageParts.push({
        inline_data: {
          mime_type: "image/jpeg",
          data: base64Data
        }
      });
    }

    const apiKey = getActiveGeminiKey(0);
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${apiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system_instruction: {
            parts: [{ text: systemPrompt }]
          },
          contents: [
            { role: "user", parts: messageParts },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
            candidateCount: 1,
          },
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(`API Error: ${data.error?.message || 'Unknown error'}`);
    }

    return data?.candidates?.[0]?.content?.parts?.[0]?.text || "عذرًا، لم أتمكن من الرد. حاول مرة أخرى!";
  } catch (error) {
    console.error("Error calling AI Assistant (Gemini):", error);

    // Fallback cascade: 1st try second Gemini key, 2nd try third Gemini key, 3rd try Groq
    try {
      const { fish, aquariums, plants, foods } = await fetchAllProducts();
      const conversationContext = conversationHistory.length > 0
        ? conversationHistory.slice(-3).map(msg =>
          `${msg.sender === 'customer' ? 'عميل' : 'أنت'}: ${(msg.text || '🎤 صوت').substring(0, 100)}`
        ).join('\n')
        : '';
      const greetingInstruction = hasGreeted
        ? "تجنب تكرار الترحيب في كل رد. ركز على الإجابة المباشرة على السؤال."
        : "يمكنك الترحيب بالعميل مرة واحدة فقط في بداية المحادثة.";
      const systemPrompt = createSystemPrompt(fish, aquariums, plants, foods, conversationContext, greetingInstruction);

      // 1. Try second Gemini key (different quota pool)
      console.log("Attempting fallback to second Gemini key...");
      const geminiReply = await callGeminiFallback(systemPrompt, userText);
      if (geminiReply) {
        console.log("Second Gemini key fallback successful.");
        return geminiReply;
      }

      // 2. Try third Gemini key
      console.log("Attempting fallback to third Gemini key...");
      const gemini3Reply = await callGeminiFallback3(systemPrompt, userText);
      if (gemini3Reply) {
        console.log("Third Gemini fallback successful.");
        return gemini3Reply;
      }

      // 3. Fallback to Groq (separate reliable cloud provider)
      console.log("Attempting fallback to Groq...");
      const groqReply = await callGroqFallback(systemPrompt, userText);
      if (groqReply) {
        console.log("Groq fallback successful.");
        return groqReply;
      }
    } catch (fallbackError) {
      console.error("All fallback attempts failed:", fallbackError);
    }

    // Enhanced hardcoded fallback responses if both APIs fail
    if (userText.includes('سعر') || userText.includes('كم')) {
      return "عذرًا، لم أتمكن من جلب الأسعار حالياً. أسعار الأسماك تختلف حسب النوع والجنس (ذكر/أنثى/زوج). يمكنك التواصل معنا مباشرة للحصول على آخر الأسعار والعروض المتوفرة.";
    } else if (userText.includes('توفر') || userText.includes('متوفر')) {
      return "عذرًا، لم أتمكن من التحقق من التوفر حالياً. نحدث مخزوننا باستمرار. يرجى التواصل معنا مباشرة لمعرفة المنتجات المتوفرة والكميات.";
    }

    return "حدث خطأ تقني مؤقت. يرجى المحاولة مرة أخرى أو التواصل معنا مباشرة!";
  }
};

// Typing Animation Component
const TypingIndicator = () => (
  <div style={styles.typingContainer}>
    <div style={styles.typingDots}>
      <div style={{ ...styles.dot, animationDelay: '0s' }}></div>
      <div style={{ ...styles.dot, animationDelay: '0.2s' }}></div>
      <div style={{ ...styles.dot, animationDelay: '0.4s' }}></div>
    </div>
    <span style={styles.typingText}>المساعد يكتب...</span>
  </div>
);

// Basic Markdown Formatter for Streaming Text
const formatMarkdown = (text) => {
  if (!text) return null;

  return text.split('\n').map((line, i) => {
    const trimmedLine = line.trim();
    // Handle unordered lists
    const isListItem = trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ');
    let content = isListItem ? trimmedLine.substring(2) : line;

    // Handle bold text **bold**
    const boldParts = content.split(/(\*\*.*?\*\*)/g);
    const formattedLine = boldParts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={j} style={{ color: '#f8fafc' }}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });

    if (isListItem) {
      return (
        <div key={i} style={{ display: 'flex', margin: '6px 0', alignItems: 'flex-start' }}>
          <span style={{ marginRight: '8px', color: '#10b981', fontWeight: 'bold' }}>•</span>
          <span style={{ flex: 1 }}>{formattedLine}</span>
        </div>
      );
    }

    return (
      <div key={i} style={{ minHeight: trimmedLine === '' ? '0.8em' : 'auto', margin: '2px 0' }}>
        {formattedLine}
      </div>
    );
  });
};

// ChatGPT-Style Streaming Text Component
const StreamingText = ({ text }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);

    let index = 0;
    let timeoutId;
    let isActive = true;

    const streamNextChunk = () => {
      if (!isActive) return;

      if (index >= text.length) {
        setIsComplete(true);
        return;
      }

      // Chunk size: 1 to 3 chars for natural flow
      const chunkSize = Math.floor(Math.random() * 3) + 1;
      const nextIndex = Math.min(index + chunkSize, text.length);
      const chunk = text.slice(index, nextIndex);

      setDisplayedText(prev => prev + chunk);
      index = nextIndex;

      // Base delay: fast streaming
      let delay = Math.floor(Math.random() * 30) + 15;

      // Pause slightly on punctuation
      if (chunk.match(/[.،,!?\n]/)) {
        delay += Math.floor(Math.random() * 200) + 150;
      }

      timeoutId = setTimeout(streamNextChunk, delay);
    };

    timeoutId = setTimeout(streamNextChunk, 50); // initial delay

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [text]);

  return (
    <div style={{ position: 'relative' }}>
      {formatMarkdown(displayedText)}
      {!isComplete && (
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '14px',
            backgroundColor: '#10b981',
            marginLeft: '4px',
            verticalAlign: 'baseline',
            animation: 'chatgpt-blink 1s step-end infinite',
            boxShadow: '0 0 5px rgba(16, 185, 129, 0.5)'
          }}
        />
      )}
      <style>{`
        @keyframes chatgpt-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// Function to check and intercept orders from AI replies
const processBotReplyForOrders = async (replyText) => {
  console.log("RAW AI REPLY:", replyText);
  if (!replyText || !replyText.includes("ORDER_READY")) {
    console.log("No ORDER_READY found in reply.");
    return replyText;
  }

  let cleanReply = replyText;
  try {
    const jsonMatch = replyText.match(/\{[\s\S]*"ORDER_READY"[\s\S]*\}/);
    if (!jsonMatch) {
      console.log("ORDER_READY was found but regex didn't match the JSON block.");
      return cleanReply;
    }

    console.log("Extracted JSON:", jsonMatch[0]);
    const orderData = JSON.parse(jsonMatch[0]);

    const orderToSave = {
      totalPrice: orderData.totalPrice || 0,
      customerName: orderData.customerName || "",
      customerPhone: orderData.customerPhone || "",
      customerAddress: orderData.customerAddress || "",
      orderDate: serverTimestamp(),
      status: "Pending",
      hasDiscount: false,
      items: (orderData.items || []).map(item => ({
        type: item.type || "generic",
        name: item.name || "عنصر غير معروف",
        price: item.price || 0,
        quantity: item.quantity || 1
      }))
    };

    const docRef = await addDoc(collection(db, "orders"), orderToSave);

    // Send to order webhook
    const webhookData = {
      ...orderToSave,
      orderId: docRef.id,
      orderDate: new Date().toISOString()
    };
    const webhookUrl = process.env.REACT_APP_N8N_CHAT_ORDER_WEBHOOK_URL;
    await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(webhookData),
    }).catch(console.error);

    // Clean up reply text from AI
    cleanReply = replyText.replace(/```json[\s\S]*```/g, "").replace(jsonMatch[0], "").trim();
    if (!cleanReply) {
      cleanReply = "✅ تم تأكيد وتسجيل طلبك بنجاح! شكراً لك، سنتواصل معك قريباً لتأكيد الطلب.";
    }
  } catch (error) {
    console.error("Error processing order from bot reply:", error);
  }
  return cleanReply;
};

const ChatScreen = () => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [customerId] = useState(getOrCreateCustomerId());
  const [customerName, setCustomerName] = useState(localStorage.getItem("customerName") || "");
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);

  const notifyFirstMessage = (msgText) => {
    if (hasGreeted) return;
    try {
      const notifyUrl = process.env.REACT_APP_N8N_CHAT_NOTIFICATION_WEBHOOK_URL;
      if (notifyUrl) {
        fetch(notifyUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: msgText,
            customerName: customerName || "مستخدم",
            timestamp: new Date().toISOString()
          })
        }).catch(console.error);
      }
    } catch (err) {
      console.error("Failed to send chat notification:", err);
    }
  };
  const [soundEnabled, setSoundEnabled] = useState(
    localStorage.getItem("soundEnabled") !== "false"
  );
  const [prevMessageCount, setPrevMessageCount] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [transcribedText, setTranscribedText] = useState("");
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [tfModel, setTfModel] = useState(null);
  const [modelStatus, setModelStatus] = useState("loading"); // loading, ready, failed
  const [visionUsed, setVisionUsed] = useState(0);
  const [showFeaturePopup, setShowFeaturePopup] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const chatBoxRef = useRef(null);
  const inputRef = useRef(null);
  const audioRefs = useRef({});
  const recordingTimerRef = useRef(null);
  const recognitionRef = useRef(null);
  // Use ref to store current transcription to avoid stale state issues
  const transcribedTextRef = useRef("");

  // Format timestamp to relative time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return "الآن";
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    if (days < 7) return `منذ ${days} يوم`;

    // Format full date for older messages - Gregorian calendar (Algerian format with French-based months in Arabic)
    const arabicMonths = [
      'جانفي', 'فيفري', 'مارس', 'أفريل', 'ماي', 'جوان',
      'جويلية', 'أوت', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];

    const day = date.getDate();
    const month = arabicMonths[date.getMonth()];
    const year = date.getFullYear();
    const dateHours = date.getHours();
    const dateMinutes = date.getMinutes();
    const ampm = dateHours >= 12 ? 'م' : 'ص';
    const displayHours = dateHours % 12 || 12;
    const displayMinutes = dateMinutes.toString().padStart(2, '0');

    return `${day} ${month} ${year}، ${displayHours}:${displayMinutes} ${ampm}`;
  };


  // Check if messages should be grouped (consecutive messages from same sender)
  const shouldGroupMessage = (currentMsg, prevMsg) => {
    if (!prevMsg) return false;
    if (currentMsg.sender !== prevMsg.sender) return false;

    // Group if messages are within 2 minutes of each other
    if (currentMsg.timestamp && prevMsg.timestamp) {
      const currentTime = currentMsg.timestamp.toDate ? currentMsg.timestamp.toDate() : new Date(currentMsg.timestamp);
      const prevTime = prevMsg.timestamp.toDate ? prevMsg.timestamp.toDate() : new Date(prevMsg.timestamp);
      const diff = (currentTime - prevTime) / 1000 / 60; // minutes
      return diff < 2;
    }
    return false;
  };

  // Enhanced scroll to bottom function
  const scrollToBottom = (behavior = "smooth", force = false) => {
    if (chatBoxRef.current) {
      const chatBox = chatBoxRef.current;

      // Always scroll to bottom with slight delay to ensure content is rendered
      setTimeout(() => {
        chatBox.scrollTo({
          top: chatBox.scrollHeight,
          behavior: behavior
        });
      }, 100);
    }
  };

  // Toggle sound preference
  const toggleSound = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    localStorage.setItem("soundEnabled", newSoundEnabled.toString());

    if (newSoundEnabled) {
      playMessageSound();
    }
  };

  // Toggle like on a message
  const toggleLike = async (messageId) => {
    if (!messageId || !customerId) return;

    const chatRef = doc(db, "chats", customerId);
    const messageRef = doc(chatRef, "messages", messageId);

    try {
      const message = messages.find(m => m.id === messageId);
      const likes = (message?.likes && Array.isArray(message.likes)) ? message.likes : [];
      const isLiked = likes.includes(customerId);

      if (isLiked) {
        await updateDoc(messageRef, {
          likes: arrayRemove(customerId)
        });
      } else {
        // arrayUnion will create the array if it doesn't exist
        await updateDoc(messageRef, {
          likes: arrayUnion(customerId)
        });
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  // Find message by ID
  const findMessageById = (messageId) => {
    return messages.find(m => m.id === messageId);
  };

  // Init first-use popup on mount
  useEffect(() => {
    const seen = localStorage.getItem("diagPopupSeen");
    if (!seen) {
      const timer = setTimeout(() => setShowFeaturePopup(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!customerName) {
      // Default to Guest to avoid blocking prompt() loops
      const defaultName = "مستخدم";
      setCustomerName(defaultName);
      localStorage.setItem("customerName", defaultName);
    }

    const chatRef = doc(db, "chats", customerId);
    const messagesRef = collection(chatRef, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"));

    // Use a ref-like local behavior to track count within the persistent listener
    let lastKnownCount = prevMessageCount;

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // Check if bot has already greeted
      const botMessages = newMessages.filter(msg => msg.sender === "admin");
      setHasGreeted(botMessages.length > 0);

      if (soundEnabled && newMessages.length > lastKnownCount && lastKnownCount > 0) {
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.sender === "admin") {
          playMessageSound();
        }
      }

      setMessages(newMessages);
      setPrevMessageCount(newMessages.length);
      lastKnownCount = newMessages.length;
      scrollToBottom("smooth", true);
    });

    const unsubscribeChat = onSnapshot(chatRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const todayStr = new Date().toDateString();
        if (data.visionQuotaDate === todayStr) {
          setVisionUsed(data.visionQuotaUsed || 0);
        } else {
          setVisionUsed(0);
        }
      } else {
        setVisionUsed(0);
      }
    });

    return () => {
      unsubscribe();
      unsubscribeChat();
    };
  }, [customerId, customerName, soundEnabled, prevMessageCount]);

  useEffect(() => {
    const loadModel = async () => {
      try {
        await tf.ready();
        const model = await tf.loadGraphModel('/model/model.json');
        setTfModel(model);
        setModelStatus("ready");
        console.log("Fish disease AI model loaded successfully.");
      } catch (error) {
        setModelStatus("failed");
        console.error("AI Model loading error:", error);
      }
    };

    const timer = setTimeout(loadModel, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Add global keyframes for animations
    if (typeof document !== 'undefined') {
      const styleId = 'chatscreen-keyframes';
      if (!document.getElementById(styleId)) {
        const styleSheet = document.createElement("style");
        styleSheet.id = styleId;
        styleSheet.innerText = `
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pulse { 
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes float {
            0% { transform: translateY(0px) translateX(0px); }
            33% { transform: translateY(-20px) translateX(10px); }
            66% { transform: translateY(10px) translateX(-10px); }
            100% { transform: translateY(0px) translateX(0px); }
          }
        `;
        document.head.appendChild(styleSheet);
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom("smooth", true);
  }, [isTyping]);

  useEffect(() => {
    // Auto-scroll when messages change
    scrollToBottom("smooth", true);
  }, [messages]);



  // Convert audio blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Transcribe audio using Web Speech API (Fallback method)
  const transcribeAudioWithWebSpeech = async (audioBlob) => {
    return new Promise((resolve) => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        console.log("Speech Recognition not available");
        resolve("");
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = 'ar-SA';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        resolve(transcript.trim());
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        resolve("");
      };

      recognition.onend = () => {
        // If no result yet, resolve empty
      };

      try {
        recognition.start();
        setTimeout(() => {
          recognition.stop();
          resolve("");
        }, 10000);
      } catch (error) {
        console.error("Failed to start recognition:", error);
        resolve("");
      }
    });
  };

  // Start recording voice with real-time transcription attempt
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Reset transcription text
      setTranscribedText("");
      transcribedTextRef.current = ""; // Reset ref as well

      // Try to use Web Speech API for real-time transcription
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      let recognition = null;

      if (SpeechRecognition) {
        try {
          recognition = new SpeechRecognition();
          recognition.lang = 'ar-SA';
          recognition.continuous = false; // Changed to false so it stops when done
          recognition.interimResults = true;

          let accumulatedText = "";
          let isStopped = false;

          recognition.onresult = (event) => {
            // Don't process if already stopped
            if (isStopped) return;

            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
              const transcript = event.results[i][0].transcript;
              if (event.results[i].isFinal) {
                finalTranscript += transcript + ' ';
              } else {
                interimTranscript += transcript;
              }
            }

            if (finalTranscript && !isStopped) {
              accumulatedText += finalTranscript;
              // Update both state and ref to keep them in sync
              setTranscribedText(accumulatedText);
              transcribedTextRef.current = accumulatedText;
            } else if (interimTranscript && !isStopped) {
              // Show interim results too
              const currentText = accumulatedText + interimTranscript;
              setTranscribedText(currentText);
              transcribedTextRef.current = accumulatedText; // Only store final text in ref
            }
          };

          recognition.onerror = (event) => {
            console.log("Speech recognition error:", event.error);
            isStopped = true;
          };

          recognition.onend = () => {
            // Don't restart automatically - we control it manually
            isStopped = true;
            // Ensure final accumulated text is saved to ref when recognition ends
            if (accumulatedText) {
              transcribedTextRef.current = accumulatedText;
              setTranscribedText(accumulatedText);
            }
          };

          recognition.start();
          recognitionRef.current = recognition;

          // Store stop flag on recognition object for cleanup
          recognition._stopFlag = { isStopped: false, set: (val) => { isStopped = val; } };
        } catch (recognitionError) {
          console.log("Could not start speech recognition:", recognitionError);
        }
      }

      // Find supported mime type for this browser (Safari uses audio/mp4, others audio/webm)
      const mimeTypes = ['audio/webm', 'audio/mp4', 'audio/aac', ''];
      let selectedMimeType = '';
      for (const type of mimeTypes) {
        if (type === '' || MediaRecorder.isTypeSupported(type)) {
          selectedMimeType = type;
          break;
        }
      }

      const recorder = new MediaRecorder(stream, selectedMimeType ? { mimeType: selectedMimeType } : undefined);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        // Wait a bit for recognition to finalize if still running
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
            // Give recognition a moment to finalize the last result
            await new Promise(resolve => setTimeout(resolve, 300));
          } catch (e) {
            console.log("Error stopping recognition:", e);
          }
          recognitionRef.current = null;
        }

        const audioBlob = new Blob(chunks, { type: selectedMimeType || 'audio/webm' });
        const base64Audio = await blobToBase64(audioBlob);
        const actualMimeType = selectedMimeType || 'audio/webm';

        // Get final transcription from ref (always current, not stale state)
        // Wait a bit more to ensure recognition.onend has fired and updated the ref
        await new Promise(resolve => setTimeout(resolve, 200));

        // Use ref value which is always current (not stale state)
        const finalTranscription = transcribedTextRef.current.trim();

        // Reset transcription display and ref
        setTranscribedText("");
        transcribedTextRef.current = "";

        // Stop all tracks first
        stream.getTracks().forEach(track => track.stop());

        // Send voice message with transcription if available, pass mimeType
        await sendVoiceMessage(base64Audio, finalTranscription, actualMimeType);
      };

      recorder.start(1000); // Collect data every second to be safer
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);

      // Start recording timer - Fixed to update properly
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      // Use a more reliable timer approach
      let startTime = Date.now();
      recordingTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setRecordingTime(elapsed);
      }, 100); // Update every 100ms for smoother display
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("لا يمكن الوصول إلى الميكروفون. يرجى التحقق من الإعدادات.");
    }
  };

  // Stop recording - Optimized for faster processing
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      // Clear timer immediately
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      // Stop recognition immediately if running
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.abort();
        } catch (e) {
          console.log("Error stopping recognition:", e);
        }
        recognitionRef.current = null;
      }

      // Stop recording
      if (mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }

      setIsRecording(false);
      // Keep the time display for a moment before resetting
      setTimeout(() => setRecordingTime(0), 500);
    }
  };



  // Call AI Assistant with audio input
  const callChatAssistantWithAudio = async (audioBase64, conversationHistory = [], hasGreeted = false, mimeType = "audio/webm") => {
    try {
      // Fetch all products first
      const { fish, aquariums, plants, foods } = await fetchAllProducts();

      // Build conversation context
      const conversationContext = conversationHistory.length > 0
        ? `\n### سياق المحادثة السابقة:\n${conversationHistory.slice(-4).map(msg =>
          `${msg.sender === 'customer' ? 'العميل' : 'المساعد'}: ${msg.text || '🎤 رسالة صوتية'}`
        ).join('\n')}\n`
        : '';

      const greetingInstruction = hasGreeted
        ? "تجنب تكرار الترحيب في كل رد. ركز على الإجابة المباشرة على السؤال."
        : "يمكنك الترحيب بالعميل مرة واحدة فقط في بداية المحادثة.";

      const systemPrompt = createSystemPrompt(fish, aquariums, plants, foods, conversationContext, greetingInstruction);

      // Extract base64 data without data URL prefix
      const base64Data = audioBase64.includes(',') ? audioBase64.split(',')[1] : audioBase64;
      console.log(`Audio Payload Size: ${Math.round(base64Data.length / 1024)} KB`);

      const apiKeys = [
        getActiveGeminiKey(0),
        getActiveGeminiKey(1),
        getActiveGeminiKey(2)
      ].filter(Boolean);

      for (let i = 0; i < apiKeys.length; i++) {
        try {
          console.log(`Attempting audio processing with API Key ${i + 1}...`);
          const response = await fetch(
            `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${apiKeys[i]}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                system_instruction: {
                  parts: [{ text: systemPrompt }]
                },
                contents: [
                  {
                    role: "user",
                    parts: [
                      {
                        inline_data: {
                          mime_type: mimeType,
                          data: base64Data
                        }
                      },
                      { text: "هذه رسالة صوتية من العميل. استمع إليها جيداً وقم بالرد عليه باللهجة الجزائرية (الدارجة) بناءً على تعليماتك كخبير في الأسماك." }
                    ]
                  }
                ],
                generationConfig: {
                  temperature: 0.7,
                  topK: 40,
                  topP: 0.95,
                  maxOutputTokens: 2048,
                },
              }),
            }
          );

          const data = await response.json();

          if (response.ok && data?.candidates?.[0]?.content?.parts?.[0]?.text) {
            console.log(`Audio processing successful with Key ${i + 1}`);
            return data.candidates[0].content.parts[0].text;
          } else {
            console.log(`Key ${i + 1} failed:`, data.error?.message || "Unknown error");
          }
        } catch (audioError) {
          console.log(`Error with Key ${i + 1}:`, audioError);
        }
      }

      // If all keys fail
      return "عذرًا، يبدو أن هناك ضغطًا كبيرًا على خوادم الصوت حاليًا. يرجى محاولة كتابة رسالتك أو المحاولة لاحقًا.";

    } catch (error) {
      console.error("Error calling AI Assistant with audio:", error);
      return "عذرًا، حدث خطأ في معالجة الرسالة الصوتية. يرجى المحاولة مرة أخرى.";
    }
  };

  // Send voice message - Optimized for faster response
  const sendVoiceMessage = async (audioBase64, transcribedText = "", mimeType = "audio/webm") => {
    if (!audioBase64 || isSending) return;

    setIsSending(true);
    const chatRef = doc(db, "chats", customerId);

    // Run database operations and sound in parallel for speed
    await Promise.all([
      setDoc(chatRef, { customerId, customerName, unread: true }, { merge: true }),
      soundEnabled ? Promise.resolve(playSendSound()) : Promise.resolve()
    ]);

    // Store transcription if available
    const messageText = transcribedText.trim() || "🎤 رسالة صوتية";

    // Send message and start typing indicator simultaneously
    const messagePromise = addDoc(collection(chatRef, "messages"), {
      audioData: audioBase64,
      messageType: "voice",
      sender: "customer",
      timestamp: serverTimestamp(),
      text: messageText,
      transcribedText: transcribedText.trim() || null
    });

    setIsTyping(true);
    scrollToBottom("smooth", true);

    // Wait for message to be saved
    await messagePromise;

    // Optimize API call - use transcription if available (much faster)
    // Note: transcribedText parameter is passed directly from the recorder.onstop
    // which uses transcribedTextRef.current to avoid stale state
    if (!hasGreeted) notifyFirstMessage(messageText);
    let botReply;
    if (transcribedText && transcribedText.trim()) {
      // Use transcribed text directly - much faster and more accurate
      botReply = await callChatAssistant(transcribedText.trim(), messages, hasGreeted);
    } else {
      // Fallback to audio API only if transcription failed
      botReply = await callChatAssistantWithAudio(audioBase64, messages, hasGreeted, mimeType);
    }

    botReply = await processBotReplyForOrders(botReply);
    setIsTyping(false);

    await addDoc(collection(chatRef, "messages"), {
      text: botReply,
      messageType: "text",
      sender: "admin",
      timestamp: serverTimestamp(),
    });

    if (!hasGreeted) {
      setHasGreeted(true);
    }

    setIsSending(false);
    scrollToBottom("smooth", true);
  };

  // Play/pause audio
  const toggleAudio = (messageId, audioData) => {
    if (!audioData) return;

    const audioElement = audioRefs.current[messageId];

    if (playingAudioId === messageId && audioElement) {
      // Pause current audio
      audioElement.pause();
      setPlayingAudioId(null);
    } else {
      // Stop any other playing audio
      Object.values(audioRefs.current).forEach(audio => {
        if (audio && !audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
      });

      // Play new audio
      if (!audioElement) {
        const audio = new Audio(audioData);
        audioRefs.current[messageId] = audio;
        audio.onended = () => setPlayingAudioId(null);
        audio.play();
      } else {
        audioElement.play();
      }
      setPlayingAudioId(messageId);
    }
  };

  // Upload image to Cloudinary
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      return null;
    }
  };

  const runModelInference = async (imageSrc) => {
    if (!tfModel) return null;

    try {
      setIsAnalyzing(true);

      const img = new Image();
      img.src = imageSrc;
      await new Promise(resolve => { img.onload = resolve; });

      // Input must be [0, 255] float32 — EfficientNet preprocess_input is baked into the model
      const imgTensor = tf.browser.fromPixels(img)
        .resizeNearestNeighbor([224, 224])
        .toFloat()
        .expandDims();

      const predTensor = tfModel.predict(imgTensor);
      const predictions = await predTensor.data(); // works for both LayersModel and GraphModel
      imgTensor.dispose();
      predTensor.dispose();

      const results = FISH_DISEASE_CLASSES
        .map((cls, i) => ({ ...cls, confidence: predictions[i] }))
        .filter(r => r.confidence > 0.10)
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 3);

      console.log("--- Local AI Model (fish_disease_model.keras) ---");
      console.table(results);

      setIsAnalyzing(false);
      return results.length > 0 ? results : null;
    } catch (error) {
      console.error("Inference error:", error);
      setIsAnalyzing(false);
      return null;
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendImageMessage = async () => {
    if (!selectedImage || isSending) return;

    setIsSending(true);
    setIsUploading(true);
    const chatRef = doc(db, "chats", customerId);

    // Upload to Cloudinary first for persistence
    const imageUrl = await uploadToCloudinary(selectedImage);
    if (!imageUrl) {
      alert("فشل رفع الصورة. يرجى المحاولة مرة أخرى.");
      setIsSending(false);
      setIsUploading(false);
      return;
    }

    const base64Image = imagePreview;
    const userText = message.trim();

    // ── Check Gemini Vision quota ───────────────────────────────────────────
    const premiumAvailable = visionUsed < VISION_DAILY_LIMIT;
    const diagMode = premiumAvailable ? "premium" : "free";
    console.log(`[Diagnostics] Mode: ${diagMode} | Used today: ${visionUsed}/${VISION_DAILY_LIMIT}`);

    // 1. Run local AI model inference (always - unlimited)
    let localModelResults = null;
    console.log("Checking if AI Model is loaded:", !!tfModel);
    if (tfModel) {
      console.log("Starting model inference...");
      localModelResults = await runModelInference(base64Image);
      console.log("Inference complete. Results:", localModelResults);
    } else {
      console.log("AI Model not loaded, skipping local inference.");
    }

    const messageData = {
      imageUrl: imageUrl,
      messageType: "image",
      sender: "customer",
      timestamp: serverTimestamp(),
      text: userText || "🖼️ صورة",
      modelAnalysis: localModelResults,
      diagMode
    };

    await addDoc(collection(chatRef, "messages"), messageData);

    setSelectedImage(null);
    setImagePreview(null);
    setMessage("");
    setIsUploading(false);
    setIsTyping(true);
    scrollToBottom("smooth", true);

    // Helper to increment quota in Firestore
    const incrementFirestoreQuota = async () => {
      const newUsed = visionUsed + 1;
      setVisionUsed(newUsed);
      await setDoc(chatRef, {
        visionQuotaUsed: newUsed,
        visionQuotaDate: new Date().toDateString()
      }, { merge: true });
    };

    // 2. Build prompt and decide whether to send image to Gemini
    if (!hasGreeted) notifyFirstMessage(userText || "🖼️ صورة");
    let botReply;
    if (localModelResults && localModelResults.length > 0) {
      // Format as internal clinical data — Gemini will use it silently (see SYMPTOM_ANALYSIS_PROMPT)
      const visualAnalysis = localModelResults
        .map(r => `${r.label} (${(r.confidence * 100).toFixed(1)}%, ${r.urgency} urgency, ${r.category})`)
        .join("; ");

      if (premiumAvailable) {
        // PREMIUM: Gemini sees the image AND gets the internal pixel-pattern hints
        const prompt = `[VISUAL_ANALYSIS: ${visualAnalysis}]. ${userText || "Please analyze this fish for diseases and symptoms."}`;
        botReply = await callChatAssistant(prompt, messages, hasGreeted, base64Image);
        await incrementFirestoreQuota();
      } else {
        // FREE TIER: no image sent — Gemini reasons only on the pixel-pattern hints
        const prompt = `[VISUAL_ANALYSIS: ${visualAnalysis}]. ${userText || "يرجى تشخيص حالة السمكة بناءً على البيانات المتاحة واسأل عن الأعراض السلوكية."}`;
        botReply = await callChatAssistant(prompt, messages, hasGreeted, null);
      }
    } else {
      // No local model results — Gemini works from image alone (or gives general guidance)
      if (premiumAvailable) {
        const prompt = userText || "Please analyze this fish for diseases and symptoms.";
        botReply = await callChatAssistant(prompt, messages, hasGreeted, base64Image);
        await incrementFirestoreQuota();
      } else {
        const prompt = `[المستخدم أرسل صورة لسمكته]. ${userText || "قدم نصائح عامة لأمراض الأسماك وأسئلة لتشخيص الحالة."}`;
        botReply = await callChatAssistant(prompt, messages, hasGreeted, null);
      }
    }

    botReply = await processBotReplyForOrders(botReply);

    // Send notification to n8n for disease detection
    try {
      const diseaseNotifyUrl = process.env.REACT_APP_N8N_DISEASE_NOTIFICATION_WEBHOOK_URL;
      if (diseaseNotifyUrl) {
        fetch(diseaseNotifyUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            imageUrl: imageUrl,
            userText: userText,
            customerName: customerName || "مستخدم",
            localModelResults: localModelResults,
            botReply: botReply,
            timestamp: new Date().toISOString()
          })
        }).catch(console.error);
      }
    } catch (err) {
      console.error("Failed to send disease notification:", err);
    }

    setIsTyping(false);

    await addDoc(collection(chatRef, "messages"), {
      text: botReply,
      messageType: "text",
      sender: "admin",
      timestamp: serverTimestamp(),
    });

    if (!hasGreeted) setHasGreeted(true);
    setIsSending(false);
    scrollToBottom("smooth", true);
  };

  const sendMessage = async () => {
    if (selectedImage) {
      await sendImageMessage();
      return;
    }
    if (!message.trim() || isSending) return;

    setIsSending(true);
    const chatRef = doc(db, "chats", customerId);
    await setDoc(chatRef, { customerId, customerName, unread: true }, { merge: true });

    if (soundEnabled) {
      playSendSound();
    }

    const messageData = {
      text: message,
      messageType: "text",
      sender: "customer",
      timestamp: serverTimestamp(),
    };

    // Add reply data if replying to a message
    if (replyingTo) {
      const repliedMessage = findMessageById(replyingTo);
      if (repliedMessage) {
        messageData.replyTo = replyingTo;
        messageData.replyToText = repliedMessage.text || (repliedMessage.messageType === "voice" ? "🎤 رسالة صوتية" : "");
        messageData.replyToSender = repliedMessage.sender;
      }
    }

    await addDoc(collection(chatRef, "messages"), messageData);

    const userText = message;
    setMessage("");
    setReplyingTo(null); // Clear reply state
    setIsTyping(true);
    scrollToBottom("smooth", true);

    // Pass conversation history and greeting status to AI
    if (!hasGreeted) notifyFirstMessage(userText);
    let botReply = await callChatAssistant(userText, messages, hasGreeted);
    botReply = await processBotReplyForOrders(botReply);
    setIsTyping(false);

    await addDoc(collection(chatRef, "messages"), {
      text: botReply,
      messageType: "text",
      sender: "admin",
      timestamp: serverTimestamp(),
    });

    // Update greeting status after first bot response
    if (!hasGreeted) {
      setHasGreeted(true);
    }

    setIsSending(false);
    scrollToBottom("smooth", true);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    const currentAudioRefs = audioRefs.current;
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (mediaRecorder?.state === 'recording') {
        mediaRecorder.stop();
      }
      Object.values(currentAudioRefs).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, [mediaRecorder]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = newHeight + 'px';
  };

  return (
    <div style={styles.container}>

      {/* Background decoration */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.03,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        zIndex: 0
      }}></div>

      {/* Custom Styles */}
      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-20px) translateX(10px); }
          66% { transform: translateY(10px) translateX(-10px); }
        }


        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Mobile touch improvements */
        @media (max-width: 768px) {
          .scroll-to-top-button {
            bottom: 90px;
            right: 16px;
            width: 44px;
            height: 44px;
          }
          
          .message-actions {
            position: fixed;
            bottom: 80px;
            left: 50%;
            transform: translateX(-50%);
          }
        }
        
        /* Smooth hover transitions */
        button:hover {
          transform: scale(1.05);
        }
        
        button:active {
          transform: scale(0.95);
        }
        
        /* Better touch targets for mobile */
        @media (max-width: 768px) {
          button {
            min-width: 44px;
            min-height: 44px;
          }
          
          .message-actions {
            position: relative !important;
            left: auto !important;
            right: auto !important;
            margin-top: 8px;
          }
        }
        
        /* Smooth hover transitions for action buttons */
        .message-actions button:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        }
        
        /* Improve message hover effect */
        .message-wrapper:hover .message-actions {
          opacity: 1;
          visibility: visible;
        }
          .chat-box::-webkit-scrollbar {
  width: 6px;
}
.chat-box::-webkit-scrollbar-track {
  background: rgba(51, 65, 85, 0.3);
  border-radius: 10px;
}
.chat-box::-webkit-scrollbar-thumb {
  background: rgba(16, 185, 129, 0.5);
  border-radius: 10px;
}
.chat-box::-webkit-scrollbar-thumb:hover {
  background: rgba(16, 185, 129, 0.7);
}

      `}</style>

      {/* Feature Intro Popup */}
      {showFeaturePopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'linear-gradient(145deg, #0f1c2e, #1a2f4a)',
            border: '1px solid rgba(16,185,129,0.4)',
            borderRadius: '24px',
            padding: '32px 28px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(16,185,129,0.1)',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Icon */}
            <div style={{
              width: '72px', height: '72px',
              background: 'linear-gradient(135deg, #10b981, #06b6d4)',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '32px',
              boxShadow: '0 0 30px rgba(16,185,129,0.5)'
            }}>🔬</div>

            {/* Title */}
            <h2 style={{ color: '#f0fdf4', fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
              تشخيص الأمراض بالذكاء الاصطناعي
            </h2>
            <p style={{ color: '#6ee7b7', fontSize: '13px', marginBottom: '24px', lineHeight: 1.5 }}>
              مميزة جديدة في ClickFishDZ!
            </p>

            {/* Feature rows */}
            {[
              { icon: '✨', label: '5 تشخيصات بالصورة يومياً', sub: 'محلل الصور المتقدم من ClickFish AI — أعلى دقة' },
              { icon: '🤖', label: 'تشخيص نصي غير محدود', sub: 'نموذج ClickFish AI المدرَّب على أمراض الأسماك' },
              { icon: '🔄', label: 'يتجدد تلقائياً كل يوم', sub: 'رصيدك يُستعاد يومياً لضمان استمرارية الخدمة' },
            ].map(({ icon, label, sub }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'flex-start', gap: '14px',
                background: 'rgba(16,185,129,0.07)',
                borderRadius: '14px', padding: '12px 16px',
                marginBottom: '10px', textAlign: 'right'
              }}>
                <span style={{ fontSize: '22px', flexShrink: 0 }}>{icon}</span>
                <div>
                  <div style={{ color: '#e2f5ec', fontSize: '14px', fontWeight: 600 }}>{label}</div>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{sub}</div>
                </div>
              </div>
            ))}

            {/* Counter preview */}
            <div style={{
              display: 'flex', justifyContent: 'center', gap: '8px',
              margin: '20px 0 24px'
            }}>
              {Array.from({ length: VISION_DAILY_LIMIT }).map((_, i) => (
                <div key={i} style={{
                  width: '36px', height: '8px',
                  borderRadius: '4px',
                  background: 'linear-gradient(90deg, #10b981, #06b6d4)',
                  boxShadow: '0 0 8px rgba(16,185,129,0.5)'
                }} />
              ))}
            </div>
            <p style={{ color: '#64748b', fontSize: '11px', marginBottom: '24px' }}>
              شريط التشخيصات المتبقية يظهر في الأعلى
            </p>

            {/* CTA Button */}
            <button
              onClick={() => {
                localStorage.setItem("diagPopupSeen", "true");
                setShowFeaturePopup(false);
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                border: 'none', borderRadius: '14px',
                color: '#fff', fontSize: '16px', fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(16,185,129,0.4)',
                transition: 'transform 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              فهمت، لنبدأ! 🐠
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ ...styles.header, position: 'relative', zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          <FaArrowLeft size={18} />
        </button>
        <div style={styles.headerContent}>
          <div style={styles.headerIcon}>
            <FaRobot size={24} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={styles.headerTitle}>مساعد Clickfish DZ</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {/* AI status dot */}
              <span style={{
                fontSize: '10px',
                color: modelStatus === 'ready' ? '#10b981' : modelStatus === 'failed' ? '#ef4444' : '#f59e0b',
                display: 'inline-flex', alignItems: 'center', gap: '4px'
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  backgroundColor: modelStatus === 'ready' ? '#10b981' : modelStatus === 'failed' ? '#ef4444' : '#f59e0b',
                  boxShadow: modelStatus === 'ready' ? '0 0 5px #10b981' : 'none'
                }} />
                {modelStatus === 'ready' ? 'الذكاء الاصطناعي: جاهز' : modelStatus === 'failed' ? 'غير متصل' : 'جاري التحميل...'}
              </span>
            </div>
          </div>
        </div>

        {/* Diagnostic quota badge */}
        <button
          onClick={() => {
            localStorage.removeItem("diagPopupSeen");
            setShowFeaturePopup(true);
          }}
          title="تشخيصات متبقية اليوم"
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            background: visionUsed >= VISION_DAILY_LIMIT
              ? 'rgba(239,68,68,0.12)'
              : 'rgba(16,185,129,0.12)',
            border: `1px solid ${visionUsed >= VISION_DAILY_LIMIT ? 'rgba(239,68,68,0.35)' : 'rgba(16,185,129,0.35)'}`,
            borderRadius: '12px',
            padding: '5px 10px',
            cursor: 'pointer',
            gap: '4px',
            marginLeft: '6px',
            minWidth: '64px'
          }}
        >
          {/* Pip bar */}
          <div style={{ display: 'flex', gap: '3px' }}>
            {Array.from({ length: VISION_DAILY_LIMIT }).map((_, i) => (
              <div key={i} style={{
                width: '8px', height: '4px',
                borderRadius: '2px',
                background: i < (VISION_DAILY_LIMIT - visionUsed)
                  ? 'linear-gradient(90deg,#10b981,#06b6d4)'
                  : 'rgba(100,116,139,0.4)',
                boxShadow: i < (VISION_DAILY_LIMIT - visionUsed) ? '0 0 4px rgba(16,185,129,0.6)' : 'none',
                transition: 'all 0.3s'
              }} />
            ))}
          </div>
          {/* Label */}
          <span style={{
            fontSize: '9px', fontWeight: 600,
            color: visionUsed >= VISION_DAILY_LIMIT ? '#f87171' : '#6ee7b7',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap'
          }}>
            {visionUsed >= VISION_DAILY_LIMIT
              ? 'وضع مجاني 🔄'
              : `${VISION_DAILY_LIMIT - visionUsed} تشخيص متبقي`
            }
          </span>
        </button>

        <button onClick={toggleSound} style={styles.soundButton}>
          {soundEnabled ? <FaVolumeUp size={18} /> : <FaVolumeMute size={18} />}
        </button>
        <div style={styles.onlineIndicator}></div>
      </div>

      {/* Free-tier banner shown inside chat when quota hit */}
      {visionUsed >= VISION_DAILY_LIMIT && (
        <div style={{
          background: 'linear-gradient(90deg, rgba(239,68,68,0.1), rgba(245,158,11,0.1))',
          borderBottom: '1px solid rgba(239,68,68,0.2)',
          padding: '7px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '8px', zIndex: 9, position: 'relative'
        }}>
          <span style={{ fontSize: '14px' }}>⚠️</span>
          <span style={{ color: '#fbbf24', fontSize: '11px', fontWeight: 500, textAlign: 'center' }}>
            لقد استهلكت تشخيصاتك اليومية بالرؤية. الآن تُستخدم أداة التشخيص المحلية الأقل دقة. يتجدد غداً عند منتصف الليل.
          </span>
        </div>
      )}

      {/* Chat Messages */}
      <div ref={chatBoxRef} style={{ ...styles.chatBox, position: 'relative', zIndex: 5 }} className="chat-box">
        {messages.length === 0 && (
          <div style={styles.welcomeMessage}>
            <div style={styles.welcomeIcon}>🐠</div>
            <h3 style={styles.welcomeTitle}>مرحبًا بك في خدمة العملاء!</h3>
            <p style={styles.welcomeText}>
              أنا هنا لمساعدتك في كل ما يتعلق بأسماك الزينة والنباتات المائية وأحواض السمك
            </p>
            <div style={styles.quickActions}>
              <button
                style={styles.quickActionBtn}
                onClick={() => setMessage("ما هي أنواع الأسماك المتوفرة؟")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(6, 182, 212, 0.2))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(6, 182, 212, 0.1))';
                }}
              >
                🐟 الأسماك المتوفرة
              </button>
              <button
                style={styles.quickActionBtn}
                onClick={() => setMessage("أحتاج مساعدة في اختيار حوض مناسب")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(6, 182, 212, 0.2))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(6, 182, 212, 0.1))';
                }}
              >
                🏺 اختيار حوض
              </button>
              <button
                style={styles.quickActionBtn}
                onClick={() => setMessage("كيف أعتني بأسماك الزينة؟")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(6, 182, 212, 0.2))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(6, 182, 212, 0.1))';
                }}
              >
                💡 نصائح العناية
              </button>
            </div>
          </div>
        )}

        {messages.map((msg, index) => {
          const prevMsg = index > 0 ? messages[index - 1] : null;
          const isGrouped = shouldGroupMessage(msg, prevMsg);
          const nextMsg = index < messages.length - 1 ? messages[index + 1] : null;
          const isLastInGroup = !nextMsg || !shouldGroupMessage(nextMsg, msg);

          return (
            <div
              key={msg.id}
              style={{
                ...styles.messageWrapper,
                ...(msg.sender === "customer" ? styles.customerMessageWrapper : styles.adminMessageWrapper),
                ...(isGrouped ? styles.groupedMessage : {}),
                ...(isLastInGroup ? { marginBottom: "8px" } : {}),
                animation: 'fadeIn 0.3s ease-out'
              }}
              onMouseEnter={() => setHoveredMessageId(msg.id)}
              onMouseLeave={() => setHoveredMessageId(null)}
            >
              {!isGrouped && (
                <div style={{
                  ...styles.messageAvatar,
                  ...(msg.sender === "customer" ? styles.customerAvatar : styles.adminAvatar)
                }}>
                  {msg.sender === "customer" ? (
                    <FaUser size={16} />
                  ) : (
                    <FaRobot size={16} />
                  )}
                </div>
              )}
              {isGrouped && <div style={{ width: '28px', flexShrink: 0 }} />}
              <div
                style={{
                  ...styles.message,
                  ...(msg.sender === "customer" ? styles.customerMessage : styles.adminMessage),
                  ...(hoveredMessageId === msg.id ? styles.messageHovered : {}),
                  position: 'relative'
                }}
              >
                {/* Reply Preview */}
                {msg.replyTo && msg.replyToText && (
                  <div style={{
                    ...styles.replyPreview,
                    ...(msg.sender === "customer" ? styles.customerReplyPreview : styles.adminReplyPreview)
                  }}>
                    <div style={styles.replyPreviewSender}>
                      {msg.replyToSender === "customer" ? "أنت" : "المساعد"}
                    </div>
                    <div style={styles.replyPreviewText}>
                      {msg.replyToText.length > 50
                        ? msg.replyToText.substring(0, 50) + "..."
                        : msg.replyToText}
                    </div>
                  </div>
                )}

                {/* Message Action Buttons - appear on hover */}
                {hoveredMessageId === msg.id && (
                  <div
                    className="message-actions"
                    style={{
                      ...styles.messageActions,
                      ...(msg.sender === "customer" ? styles.customerMessageActions : styles.adminMessageActions)
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLike(msg.id);
                      }}
                      style={{
                        ...styles.actionButton,
                        ...(msg.likes && msg.likes.includes && msg.likes.includes(customerId) ? styles.actionButtonActive : {})
                      }}
                      title="إعجاب"
                    >
                      <FaHeart size={12} />
                      {msg.likes && Array.isArray(msg.likes) && msg.likes.length > 0 && (
                        <span style={styles.likeCount}>{msg.likes.length}</span>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setReplyingTo(msg.id);
                        if (inputRef.current) {
                          inputRef.current.focus();
                        }
                      }}
                      style={styles.actionButton}
                      title="رد"
                    >
                      <FaReply size={12} />
                    </button>
                  </div>
                )}

                {/* Like indicator - show if message has likes */}
                {msg.likes && Array.isArray(msg.likes) && msg.likes.length > 0 && hoveredMessageId !== msg.id && (
                  <div style={{
                    ...styles.likeIndicator,
                    ...(msg.sender === "customer" ? styles.customerLikeIndicator : styles.adminLikeIndicator)
                  }}>
                    <FaHeart size={10} style={{ color: "#ef4444" }} />
                    <span style={styles.likeCountSmall}>{msg.likes.length}</span>
                  </div>
                )}

                {msg.messageType === "image" && msg.imageUrl ? (
                  <div style={styles.imageMessageContainer}>
                    <img
                      src={msg.imageUrl}
                      alt="Fish"
                      style={styles.messageImage}
                      onClick={() => window.open(msg.imageUrl, '_blank')}
                    />
                    {msg.text && msg.text !== "🖼️ صورة" && (
                      <div style={styles.imageCaption}>{msg.text}</div>
                    )}
                  </div>
                ) : msg.messageType === "voice" && msg.audioData ? (
                  <div style={styles.voiceMessageContainer}>
                    <button
                      onClick={() => toggleAudio(msg.id, msg.audioData)}
                      style={styles.voicePlayButton}
                    >
                      {playingAudioId === msg.id ? (
                        <FaPause size={14} />
                      ) : (
                        <FaPlay size={14} />
                      )}
                    </button>
                    <div style={styles.voiceMessageInfo}>
                      <div style={styles.voiceLabel}>🎤 رسالة صوتية</div>
                      {msg.transcribedText && (
                        <div style={{
                          fontSize: "12px",
                          color: "rgba(255, 255, 255, 0.9)",
                          marginTop: "4px",
                          padding: "4px 8px",
                          background: "rgba(16, 185, 129, 0.2)",
                          borderRadius: "6px",
                          fontStyle: "italic"
                        }}>
                          "{msg.transcribedText}"
                        </div>
                      )}
                      {!msg.transcribedText && (
                        <div style={styles.voiceDuration}>اضغط للاستماع</div>
                      )}
                    </div>
                    <audio
                      ref={(el) => {
                        if (el && msg.audioData) {
                          audioRefs.current[msg.id] = el;
                          if (!el.src) {
                            el.src = msg.audioData;
                          }
                        }
                      }}
                      onEnded={() => setPlayingAudioId(null)}
                      onPause={() => {
                        if (playingAudioId === msg.id) setPlayingAudioId(null);
                      }}
                    />
                  </div>
                ) : (
                  <div style={styles.messageContent}>
                    {(() => {
                      // Check if message is new (less than 15 seconds old) to prevent re-animating history
                      const isNewMessage = !msg.timestamp ||
                        (msg.timestamp.toDate && (Date.now() - msg.timestamp.toDate().getTime() < 15000));
                      const isLatest = index === messages.length - 1;

                      if (msg.sender === "admin" && isLatest && isNewMessage) {
                        return <StreamingText text={msg.text} />;
                      } else {
                        // Regular static rendering for old messages with basic markdown
                        return formatMarkdown(msg.text);
                      }
                    })()}
                  </div>
                )}
                {/* Timestamp and Status */}
                <div style={{
                  ...styles.messageTimestamp,
                  ...(msg.sender === "customer" ? styles.customerTimestamp : styles.adminTimestamp)
                }}>
                  {msg.timestamp && (
                    <span>{formatTimestamp(msg.timestamp)}</span>
                  )}
                  {msg.sender === "customer" && (
                    <span style={styles.messageStatus}>
                      <FaCheck size={10} style={{ opacity: 0.7 }} />
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div style={{
            ...styles.messageWrapper,
            ...styles.adminMessageWrapper,
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{ ...styles.messageAvatar, ...styles.adminAvatar }}>
              <FaRobot size={16} />
            </div>
            <div style={{ ...styles.message, ...styles.adminMessage }}>
              <TypingIndicator />
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div style={{ ...styles.inputContainer, position: 'relative', zIndex: 10 }}>
        {/* Reply Preview in Input Area */}
        {replyingTo && (() => {
          const repliedMessage = findMessageById(replyingTo);
          return repliedMessage ? (
            <div style={styles.replyPreviewContainer}>
              <div style={styles.replyPreviewInfo}>
                <FaReply size={12} style={{ marginLeft: '8px' }} />
                <span style={styles.replyPreviewLabel}>
                  رد على {repliedMessage.sender === "customer" ? "رسالتك" : "المساعد"}
                </span>
              </div>
              <div style={styles.replyPreviewMessage}>
                {repliedMessage.text && repliedMessage.text.length > 60
                  ? repliedMessage.text.substring(0, 60) + "..."
                  : repliedMessage.text || "🎤 رسالة صوتية"}
              </div>
              <button
                onClick={() => setReplyingTo(null)}
                style={styles.closeReplyButton}
                title="إلغاء الرد"
              >
                <FaTimes size={12} />
              </button>
            </div>
          ) : null;
        })()}
        {/* Image Preview in Input Area */}
        {imagePreview && (
          <div style={styles.imagePreviewContainer}>
            <div style={styles.diseaseTag}>🔍 فحص الأمراض</div>
            <img src={imagePreview} alt="Preview" style={styles.imagePreviewThumb} />
            <button
              onClick={() => {
                setSelectedImage(null);
                setImagePreview(null);
              }}
              style={styles.closePreviewButton}
            >
              <FaTimes size={12} />
            </button>
            {isUploading && (
              <div style={styles.uploadOverlay}>
                <div style={styles.spinner}></div>
                <span>{isAnalyzing ? "جاري تحليل المرض..." : "جاري الرفع..."}</span>
              </div>
            )}
          </div>
        )}
        <div style={styles.inputWrapper}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />
          {isRecording && (
            <div style={styles.recordingIndicator}>
              <div style={styles.recordingPulse}></div>
              <span style={styles.recordingText}>
                {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
              </span>
              <span style={{
                fontSize: '11px',
                marginLeft: '8px',
                opacity: 0.9
              }}>
                ثانية
              </span>
              {transcribedText && (
                <span style={styles.transcriptionPreview}>
                  {transcribedText.substring(0, 50)}{transcribedText.length > 50 ? '...' : ''}
                </span>
              )}
            </div>
          )}
          <textarea
            ref={inputRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyPress={handleKeyPress}
            placeholder={isRecording ? "جاري التسجيل..." : "اكتب رسالتك هنا..."}
            style={{
              ...styles.textArea,
              ...(isRecording ? { opacity: 0.5, pointerEvents: 'none' } : {}),
            }}
            onFocus={(e) => {
              e.target.style.border = "2px solid rgba(16, 185, 129, 0.6)";
              e.target.style.background = "rgba(51, 65, 85, 1)";
            }}
            onBlur={(e) => {
              e.target.style.border = "2px solid rgba(71, 85, 105, 0.5)";
              e.target.style.background = "rgba(51, 65, 85, 0.9)";
            }}
            rows={1}
            disabled={isSending || isRecording}
            aria-label="اكتب رسالتك هنا"
          />
          {!isRecording ? (
            <>
              <button
                onClick={startRecording}
                style={{
                  ...styles.voiceButton,
                  ...(isSending ? styles.sendButtonDisabled : {})
                }}
                disabled={isSending}
                title="تسجيل رسالة صوتية"
              >
                <FaMicrophone size={18} />
              </button>
              <button
                onClick={() => fileInputRef.current.click()}
                style={{
                  ...styles.voiceButton,
                  background: "rgba(16, 185, 129, 0.15)",
                  color: "#10b981",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  ...(isSending ? styles.sendButtonDisabled : {})
                }}
                disabled={isSending}
                title="إرفاق صورة"
              >
                <FaImage size={18} />
              </button>
              <button
                onClick={sendMessage}
                style={{
                  ...styles.sendButton,
                  ...(isSending || (!message.trim() && !selectedImage) ? styles.sendButtonDisabled : {}),
                  ...((message.trim() || selectedImage) && !isSending ? styles.sendButtonActive : {})
                }}
                disabled={isSending || (!message.trim() && !selectedImage)}
              >
                <FaPaperPlane size={16} />
              </button>
            </>
          ) : (
            <button
              onClick={stopRecording}
              style={{
                ...styles.voiceButton,
                ...styles.recordingButton
              }}
              title="إيقاف التسجيل"
            >
              <FaStop size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    height: "100vh",
    background: "linear-gradient(135deg, #0a0e1e 0%, #1a1f2e 25%, #0f172a 50%, #1a1f2e 75%, #0a0e1e 100%)",
    backgroundSize: "400% 400%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    animation: "gradientShift 15s ease infinite"
  },
  header: {
    background: "linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.95))",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderBottom: "1px solid rgba(16, 185, 129, 0.2)",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  backButton: {
    background: "linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(6, 182, 212, 0.2))",
    border: "1px solid rgba(16, 185, 129, 0.4)",
    borderRadius: "50%",
    width: "44px",
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#10b981",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.2)",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  headerIcon: {
    background: "linear-gradient(135deg, #10b981, #06b6d4)",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
  },
  headerTitle: {
    color: "white",
    fontSize: "16px",
    fontWeight: "600",
    margin: 0,
    lineHeight: "1.2",
  },
  headerSubtitle: {
    color: "#94a3b8",
    fontSize: "12px",
    margin: 0,
    lineHeight: "1.2",
  },
  soundButton: {
    background: "rgba(16, 185, 129, 0.2)",
    border: "1px solid rgba(16, 185, 129, 0.3)",
    borderRadius: "50%",
    width: "44px",
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#10b981",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  onlineIndicator: {
    width: "12px",
    height: "12px",
    background: "#10b981",
    borderRadius: "50%",
    boxShadow: "0 0 10px rgba(16, 185, 129, 0.5)",
    animation: "pulse 2s infinite",
  },
  chatBox: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    maxWidth: "100%",
    margin: "0 auto",
    width: "100%",
    scrollBehavior: "smooth",
    paddingBottom: "80px",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(16, 185, 129, 0.3) transparent",
    background: "transparent",
  },

  welcomeMessage: {
    textAlign: "center",
    padding: "32px 24px",
    background: "linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(51, 65, 85, 0.9))",
    backdropFilter: "blur(20px)",
    borderRadius: "16px",
    border: "1px solid rgba(16, 185, 129, 0.2)",
    margin: "16px auto",
    maxWidth: "90%",
    animation: "fadeIn 0.5s ease-out",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
    position: "relative",
    overflow: "hidden",
  },
  welcomeIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    animation: "bounce 2s infinite",
    filter: "drop-shadow(0 2px 8px rgba(16, 185, 129, 0.3))",
  },
  welcomeTitle: {
    background: "linear-gradient(135deg, #10b981, #06b6d4)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "8px",
  },
  welcomeText: {
    color: "#cbd5e1",
    fontSize: "14px",
    lineHeight: "1.5",
    margin: "0 0 20px 0",
  },
  quickActions: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "center",
  },
  quickActionBtn: {
    background: "linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(6, 182, 212, 0.1))",
    border: "1px solid rgba(16, 185, 129, 0.3)",
    borderRadius: "12px",
    padding: "10px 18px",
    color: "#10b981",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    minWidth: "200px",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.15)",
  },
  messageWrapper: {
    display: "flex",
    alignItems: "flex-end",
    gap: "8px",
    maxWidth: "75%",
    marginBottom: "4px",
    marginTop: "4px",
    padding: "0 4px",
    position: "relative",
  },
  customerMessageWrapper: {
    alignSelf: "flex-end",
    flexDirection: "row-reverse",
    marginLeft: "auto",
  },
  adminMessageWrapper: {
    alignSelf: "flex-start",
    marginRight: "auto",
  },
  messageAvatar: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "12px",
    flexShrink: 0,
    border: "2px solid rgba(255, 255, 255, 0.1)",
  },
  customerAvatar: {
    background: "linear-gradient(135deg, #10b981, #06b6d4)",
  },
  adminAvatar: {
    background: "linear-gradient(135deg, rgba(71, 85, 105, 0.9), rgba(51, 65, 85, 0.9))",
  },
  message: {
    padding: "10px 14px",
    borderRadius: "18px",
    wordBreak: "break-word",
    lineHeight: "1.5",
    fontSize: "15px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    position: "relative",
    maxWidth: "100%",
    transition: "all 0.2s ease",
  },
  customerMessage: {
    background: "linear-gradient(135deg, #10b981, #06b6d4)",
    color: "white",
    borderBottomRightRadius: "4px",
    borderTopRightRadius: "18px",
    borderTopLeftRadius: "18px",
    borderBottomLeftRadius: "18px",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    maxWidth: "100%",
  },
  adminMessage: {
    background: "rgba(51, 65, 85, 0.95)",
    color: "white",
    border: "1px solid rgba(71, 85, 105, 0.4)",
    borderBottomLeftRadius: "4px",
    borderTopLeftRadius: "18px",
    borderTopRightRadius: "18px",
    borderBottomRightRadius: "18px",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
    maxWidth: "100%",
  },

  messageText: {
    whiteSpace: "pre-wrap", // Changed from "pre-line" to "pre-wrap"
    wordBreak: "break-word",
    lineHeight: "1.6",
    fontSize: "15px",
    color: "inherit",
  },
  messageContent: {
    display: "block",
    lineHeight: "1.6",
    fontSize: "15px",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    hyphens: "auto",
  },

  messageBlock: {
    marginBottom: "8px",
    display: "block",
  },

  messageLine: {
    display: "block",
    marginBottom: "4px",
  },
  typingContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  typingDots: {
    display: "flex",
    gap: "4px",
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#10b981",
    animation: "bounce 1.4s infinite ease-in-out both",
  },
  typingText: {
    color: "#94a3b8",
    fontSize: "12px",
    fontStyle: "italic",
  },
  cursor: {
    animation: "blink 1s infinite",
    color: "#10b981",
    fontWeight: "bold",
  },
  inputContainer: {
    padding: "12px 16px",
    background: "linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.95))",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderTop: "1px solid rgba(16, 185, 129, 0.15)",
    boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.2)",
    flexShrink: 0,
    position: "sticky",
    bottom: 0,
    zIndex: 100,
  },
  inputWrapper: {
    maxWidth: "100%",
    margin: "0 auto",
    display: "flex",
    gap: "8px",
    alignItems: "flex-end",
  },
  textArea: {
    flex: 1,
    padding: "10px 14px",
    background: "rgba(51, 65, 85, 0.8)",
    border: "1px solid rgba(71, 85, 105, 0.4)",
    borderRadius: "20px",
    color: "white",
    fontSize: "14px",
    resize: "none",
    maxHeight: "100px",
    minHeight: "40px",
    outline: "none",
    transition: "all 0.2s",
    scrollbarWidth: "none",
    fontFamily: "inherit",
    lineHeight: "1.4",
  },
  sendButton: {
    background: "rgba(107, 114, 128, 0.4)",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
    flexShrink: 0,
  },
  sendButtonActive: {
    background: "linear-gradient(135deg, #10b981, #06b6d4)",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    transform: "scale(1.05)",
  },
  sendButtonDisabled: {
    background: "rgba(107, 114, 128, 0.25)",
    cursor: "not-allowed",
    boxShadow: "none",
    transform: "scale(1)",
    opacity: 0.5,
  },
  voiceButton: {
    background: "rgba(59, 130, 246, 0.2)",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    color: "#60a5fa",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
    flexShrink: 0,
  },
  recordingButton: {
    background: "rgba(239, 68, 68, 0.3)",
    border: "2px solid rgba(239, 68, 68, 0.5)",
    color: "#f87171",
    animation: "pulse 1s infinite",
  },
  recordingIndicator: {
    position: "absolute",
    top: "-50px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95))",
    backdropFilter: "blur(10px)",
    padding: "10px 20px",
    borderRadius: "25px",
    color: "white",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: "0 4px 15px rgba(239, 68, 68, 0.4)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    animation: "fadeInDown 0.3s ease-out",
  },
  recordingPulse: {
    width: "12px",
    height: "12px",
    background: "#ef4444",
    borderRadius: "50%",
    animation: "pulse 1s infinite",
  },
  recordingText: {
    fontFamily: "monospace",
  },
  transcriptionPreview: {
    fontSize: "12px",
    marginLeft: "12px",
    color: "rgba(255, 255, 255, 0.9)",
    fontStyle: "italic",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  voiceMessageContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 12px",
    background: "rgba(59, 130, 246, 0.1)",
    borderRadius: "12px",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    minWidth: "200px",
  },
  voicePlayButton: {
    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    flexShrink: 0,
  },
  voiceMessageInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  voiceLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "inherit",
  },
  voiceDuration: {
    fontSize: "12px",
    color: "rgba(255, 255, 255, 0.7)",
  },
  groupedMessage: {
    marginTop: "1px",
  },
  messageHovered: {
    transform: "scale(1.01)",
    transition: "transform 0.2s ease",
  },
  messageTimestamp: {
    fontSize: "10px",
    color: "rgba(255, 255, 255, 0.6)",
    marginTop: "2px",
    fontStyle: "normal",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  customerTimestamp: {
    textAlign: "right",
    justifyContent: "flex-end",
  },
  adminTimestamp: {
    textAlign: "left",
    justifyContent: "flex-start",
  },
  messageStatus: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: "4px",
  },
  // Reply Preview Styles
  replyPreview: {
    borderLeft: "3px solid rgba(16, 185, 129, 0.6)",
    padding: "8px 10px",
    marginBottom: "8px",
    borderRadius: "8px",
    background: "rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
  },
  customerReplyPreview: {
    borderLeft: "3px solid rgba(16, 185, 129, 0.6)",
  },
  adminReplyPreview: {
    borderLeft: "3px solid rgba(100, 116, 139, 0.6)",
  },
  replyPreviewSender: {
    fontSize: "11px",
    fontWeight: "600",
    color: "rgba(255, 255, 255, 0.8)",
    marginBottom: "4px",
  },
  replyPreviewText: {
    fontSize: "12px",
    color: "rgba(255, 255, 255, 0.7)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  replyPreviewContainer: {
    background: "rgba(51, 65, 85, 0.8)",
    border: "1px solid rgba(16, 185, 129, 0.3)",
    borderRadius: "12px 12px 0 0",
    padding: "10px 14px",
    marginBottom: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    position: "relative",
  },
  replyPreviewInfo: {
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    color: "#10b981",
    fontWeight: "500",
  },
  replyPreviewLabel: {
    fontSize: "12px",
  },
  replyPreviewMessage: {
    fontSize: "13px",
    color: "rgba(255, 255, 255, 0.9)",
    padding: "6px 10px",
    background: "rgba(0, 0, 0, 0.2)",
    borderRadius: "6px",
    borderRight: "3px solid rgba(16, 185, 129, 0.5)",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  closeReplyButton: {
    position: "absolute",
    top: "8px",
    left: "8px",
    background: "rgba(239, 68, 68, 0.2)",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    color: "#f87171",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },
  // Message Actions Styles
  messageActions: {
    position: "absolute",
    display: "flex",
    gap: "6px",
    alignItems: "center",
    zIndex: 10,
    opacity: 1,
    visibility: "visible",
    transition: "opacity 0.2s ease, visibility 0.2s ease",
  },
  customerMessageActions: {
    left: "-60px",
    top: "50%",
    transform: "translateY(-50%)",
  },
  adminMessageActions: {
    right: "-60px",
    top: "50%",
    transform: "translateY(-50%)",
  },
  actionButton: {
    background: "rgba(30, 41, 59, 0.9)",
    border: "1px solid rgba(71, 85, 105, 0.5)",
    borderRadius: "20px",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "rgba(255, 255, 255, 0.7)",
    cursor: "pointer",
    transition: "all 0.2s",
    position: "relative",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
  },
  actionButtonActive: {
    background: "rgba(239, 68, 68, 0.2)",
    border: "1px solid rgba(239, 68, 68, 0.5)",
    color: "#ef4444",
  },
  likeCount: {
    position: "absolute",
    top: "-8px",
    right: "-8px",
    background: "#ef4444",
    color: "white",
    borderRadius: "10px",
    padding: "2px 6px",
    fontSize: "9px",
    fontWeight: "600",
    minWidth: "16px",
    textAlign: "center",
  },
  // Like Indicator Styles
  likeIndicator: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    background: "rgba(30, 41, 59, 0.95)",
    borderRadius: "12px",
    border: "1px solid rgba(71, 85, 105, 0.5)",
    zIndex: 5,
  },
  customerLikeIndicator: {
    bottom: "-20px",
    right: "8px",
  },
  adminLikeIndicator: {
    bottom: "-20px",
    left: "8px",
  },
  likeCountSmall: {
    fontSize: "10px",
    color: "rgba(255, 255, 255, 0.9)",
    fontWeight: "500",
  },
  // Image Message Styles
  imageMessageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxWidth: "100%",
  },
  messageImage: {
    width: "100%",
    maxWidth: "300px",
    borderRadius: "12px",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    transition: "transform 0.2s",
  },
  imageCaption: {
    fontSize: "14px",
    lineHeight: "1.4",
  },
  // Image Preview Styles
  imagePreviewContainer: {
    position: "relative",
    width: "80px",
    height: "80px",
    marginBottom: "12px",
    borderRadius: "12px",
    overflow: "hidden",
    border: "2px solid #10b981",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
    animation: "slideUp 0.3s ease-out",
  },
  imagePreviewThumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  closePreviewButton: {
    position: "absolute",
    top: "4px",
    left: "4px",
    background: "rgba(0, 0, 0, 0.6)",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
  },
  uploadOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "10px",
    gap: "4px",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTop: "2px solid #10b981",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  diseaseTag: {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    background: "rgba(16, 185, 129, 0.9)",
    color: "white",
    fontSize: "10px",
    padding: "2px 0",
    textAlign: "center",
    zIndex: 3,
    fontWeight: "bold",
  },
};

// End of ChatScreen
export default ChatScreen;